import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const SecurityContext = createContext(null);

const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout (F1.6)
const TAB_BLUR_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of tab blur triggers re-auth

export function SecurityProvider({ children }) {
  const { user, logout, accessToken, refreshToken, refresh } = useAuth();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [tabBlurred, setTabBlurred] = useState(false);
  const [blurTimestamp, setBlurTimestamp] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset timers on user activity
  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowTimeoutWarning(false);

    if (warningRef.current) clearTimeout(warningRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (user && accessToken) {
      // Warning timer - 5 minutes before expiry (F1.6)
      warningRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
      }, SESSION_TIMEOUT_MS - WARNING_BEFORE_MS);

      // Logout timer
      timeoutRef.current = setTimeout(() => {
        setShowTimeoutWarning(false);
        setSessionExpired(true);
        setShowReauthModal(true);
      }, SESSION_TIMEOUT_MS);
    }
  }, [user, accessToken]);

  // Track user activity
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => resetTimers();

    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (warningRef.current) clearTimeout(warningRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [user, resetTimers]);

  // Tab visibility change - blur protection with token refresh on return (F1.1, F1.2)
  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        setTabBlurred(true);
        setBlurTimestamp(Date.now());
      } else {
        const wasBlurred = tabBlurred;
        const blurDuration = blurTimestamp ? Date.now() - blurTimestamp : 0;
        
        setTabBlurred(false);
        setBlurTimestamp(null);
        
        if (wasBlurred && blurDuration > TAB_BLUR_TIMEOUT_MS) {
          // Try silent token refresh first (F1.2)
          if (refreshToken) {
            try {
              const newToken = await refresh();
              if (newToken) {
                // Token refreshed successfully, continue session
                resetTimers();
                return;
              }
            } catch {}
          }
          // Token refresh failed, require re-auth
          setSessionExpired(true);
          setShowReauthModal(true);
        } else {
          resetTimers();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, tabBlurred, blurTimestamp, refreshToken, refresh, resetTimers]);

  // Extend session when user clicks "Stay logged in" (F1.6)
  const extendSession = useCallback(async () => {
    // Attempt silent refresh
    if (refreshToken) {
      try {
        const newToken = await refresh();
        if (newToken) {
          resetTimers();
          return true;
        }
      } catch {}
    }
    resetTimers();
    return true;
  }, [refreshToken, refresh, resetTimers]);

  // Called after re-authentication (F1.4)
  const onReauthSuccess = useCallback(() => {
    setShowReauthModal(false);
    setSessionExpired(false);
    setFailedAttempts(0);
    resetTimers();
  }, [resetTimers]);

  // Handle re-auth failure
  const onReauthFailure = useCallback(() => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);
    // After 5 failed attempts, redirect to login (F1.4 security measure)
    if (newAttempts >= 5) {
      const currentPath = window.location.pathname;
      logout();
      window.location.href = `/auth/login?returnUrl=${encodeURIComponent(currentPath)}`;
    }
  }, [failedAttempts, logout]);

  // Handle full session expiry redirect (F1.3)
  const handleFullExpiry = useCallback(() => {
    const currentPath = window.location.pathname;
    logout();
    window.location.href = `/auth/login?returnUrl=${encodeURIComponent(currentPath)}`;
  }, [logout]);

  // Handle overlay click - attempt token refresh (F1.1, F1.2)
  const handleOverlayClick = useCallback(async () => {
    if (refreshToken) {
      try {
        const newToken = await refresh();
        if (newToken) {
          setTabBlurred(false);
          resetTimers();
          return true;
        }
      } catch {}
    }
    // Token refresh failed, show re-auth modal
    setShowReauthModal(true);
    return false;
  }, [refreshToken, refresh, resetTimers]);

  const value = {
    showTimeoutWarning,
    showReauthModal,
    tabBlurred,
    sessionExpired,
    failedAttempts,
    extendSession,
    onReauthSuccess,
    onReauthFailure,
    handleFullExpiry,
    handleOverlayClick,
    setShowReauthModal,
  };

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

export const useSecurity = () => {
  const ctx = useContext(SecurityContext);
  if (!ctx) throw new Error('useSecurity must be used within SecurityProvider');
  return ctx;
};
